import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { FaRobot } from 'react-icons/fa';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';
import { AppContext } from '../../context/AppContext';
import PublicarWarmUpButton from './PublicarWarmUpButton';

const PageContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 32px 16px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(16,19,37,0.08);
  padding: 24px;
`;

const TextArea = styled.textarea`
  min-height: 100px;
  resize: vertical;
  font-size: 1.1rem;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
`;

const Button = styled.button`
  background: #e4312e;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #c3251a; }
`;

const OutputBox = styled.div`
  background: #181c2f;
  color: #fff;
  border-radius: 10px;
  margin-top: 24px;
  padding: 20px;
  font-size: 1.15rem;
  min-height: 80px;
  white-space: pre-wrap;
`;

const ErrorMsg = styled.div`
  color: #e4312e;
  margin-top: 10px;
  font-weight: bold;
`;

const RedaccionIACircuitos: React.FC = () => {
  const { data } = useContext(AppContext);
  const { currentUser, hasAnyRole } = useAuth();

  // --- Estado del input y output IA ---
  type PromptStatus = 'pending' | 'loading' | 'success' | 'error';
  interface PromptGen {
    title: string;
    prompt: string;
    status: PromptStatus;
    result: string;
    error: string;
  }
  const [promptsGen, setPromptsGen] = useState<PromptGen[]>([]);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // --- Liga selection ---
  const [selectedLigaId, setSelectedLigaId] = useState<string>(data.ligas[0]?.id || '');
  const ligas = data.ligas;
  const liga = ligas.find(l => l.id === selectedLigaId);

  // --- Filter carreras by liga ---
  const carrerasLiga = data.carreras.filter(c => c.ligaId === selectedLigaId);

  // --- Circuit selection (only circuits in selected liga) ---
  const circuitos = Array.from(new Set(carrerasLiga.map(c => c.circuito).filter(Boolean)));
  const [selectedCircuit, setSelectedCircuit] = useState<string>(circuitos[0] || '');

  // --- Find next and last race ---
  const now = new Date();
  const carrerasOrdenadas = [...carrerasLiga].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimaCarrera = [...carrerasLiga].filter(c => new Date(c.fecha) <= now).sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())[0];
  const proximaCarrera = [...carrerasLiga].filter(c => new Date(c.fecha) > now).sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())[0];

  // --- Clasificación general ---
  // Solo carreras celebradas y publicadas
  const carrerasValidas = carrerasLiga.filter(c => new Date(c.fecha) <= now && c.publicada === true);
  const puntosPorSocio: Record<string, number> = {};
  carrerasValidas.forEach(carrera => {
    carrera.resultados.forEach(res => {
      if (!puntosPorSocio[res.socioId]) puntosPorSocio[res.socioId] = 0;
      puntosPorSocio[res.socioId] += typeof res.puntosTotales === 'number' ? res.puntosTotales : (typeof res.puntos === 'number' ? res.puntos : 0);
    });
  });
  const clasificacion: { socioId: string, nombre: string, puntos: number }[] = Object.entries(puntosPorSocio)
    .map(([socioId, puntos]) => {
      const socio = data.socios.find(s => s.id === socioId);
      return {
        socioId,
        nombre: socio?.display_name || socio?.nombre || 'Piloto',
        puntos
      };
    })
    .sort((a, b) => b.puntos - a.puntos);

  // Solo permitir acceso a admin y superadmin
  if (!currentUser || !hasAnyRole(['admin', 'superadmin'])) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#e4312e', fontWeight: 'bold', fontSize: 22 }}>
        No tienes permiso para acceder a esta página.
      </div>
    );
  }

  // Helper: tabla de clasificación en texto
  function tablaClasificacionTexto(clasificacion: { socioId: string, nombre: string, puntos: number }[]) {
    let out = 'Pos | Piloto | Puntos\n';
    out += '--- | --- | ---\n';
    clasificacion.forEach((row, idx) => {
      out += `${idx + 1} | ${row.nombre} | ${row.puntos}\n`;
    });
    return out;
  }

  // Helper: tabla de resultados de la última carrera en texto (simplificado)
  function tablaResultadosTexto(ultimaCarrera: any, data: any) {
    if (!ultimaCarrera) return '';
    let out = 'Pos | Piloto | Puntos\n';
    out += '--- | --- | ---\n';
    ultimaCarrera.resultados.sort((a: any, b: any) => a.posicion - b.posicion).forEach((res: any) => {
      const socio = data.socios.find((s: any) => s.id === res.socioId);
      out += `${res.posicion} | ${socio?.display_name || socio?.nombre || 'Piloto'} | ${res.puntosTotales ?? res.puntos}\n`;
    });
    return out;
  }

  // Información técnica del Gillet Vertigo
  const gilletVertigoInfo = `
GILLET VERTIGO - ESPECIFICACIONES TÉCNICAS:

VERSIONES PRINCIPALES:
- Vertigo Mk1 (1992-2007): Motor Alfa Romeo V6 3.0-3.6L (360 cv), Peso ≈990 kg, 0-100 km/h en 3.26s
- Vertigo .5 Spirit (2008-actualidad): Motor Maserati/Ferrari V8 4.2L (≈450 cv), Peso ≈950-1000 kg, 0-100 km/h en 3.0s
- Vertigo Streiff (race spec): Versión de competición con motor Alfa V6 4.0L o Maserati V8 4.2L, Peso ≈900 kg

CARACTERÍSTICAS TÉCNICAS CLAVE:
- Chasis monocasco de carbono de solo 58 kg (tipo F1)
- Carrocería en materiales compuestos
- Relación peso/potencia: ≈2.7 kg/cv
- Récord Guinness 1994 por aceleración 0-100 km/h

HISTORIAL EN COMPETICIÓN:
- FIA GT Championship (2005-2008): Clase G2, Campeón 2006-2008
- Belcar Belgian GT: Clase GT con homologación GT3 nacional (2007)
- 24 Horas de Spa (2002-2008): Mejor resultado P13 en 2004
- Filosofía "David contra Goliat": equipo belga pequeño compitiendo contra Porsche y Ferrari

CATEGORÍA DE COMPETICIÓN:
- GT2 early-2000s (equivalente a Porsche 996 GT3-R, Ferrari 360 N-GT)
- GT3 nacional con restricciones
- Clase G2 "non-homologated GT"
`;

  // La API key se debe poner en un .env y acceder vía variable de entorno
  const API_URL = 'https://api.deepseek.com/chat/completions';
  const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setOutput('');
    setLoading(true);
    
    // 1. Datos base
    const ligaNombre = liga?.nombre || '';
    const circuitoNombre = selectedCircuit;
    const ultimaCarreraNombre = ultimaCarrera?.nombre || '';
    const ultimaCarreraCircuito = ultimaCarrera?.circuito || '';
    const ultimaCarreraFecha = ultimaCarrera?.fecha || '';
    const tablaResultados = tablaResultadosTexto(ultimaCarrera, data);
    const tablaClasif = tablaClasificacionTexto(clasificacion);

    // 2. Prompts rediseñados
    const prompts = [
      {
        title: '📝 Resumen: La Última Carrera',
        prompt: `ESTILO REQUERIDO: Redacta un resumen conciso en UN SOLO PÁRRAFO de máximo 4-5 líneas.

Última carrera: ${ultimaCarreraNombre} (${ultimaCarreraCircuito}) - ${ultimaCarreraFecha}
Resultados:\n${tablaResultados}

Redacta un párrafo breve y directo que resuma los resultados de la última carrera, mencionando solo:
- El ganador de la carrera
- Los pilotos del podio
- Algún dato relevante si hubiera empates o resultados destacables

IMPORTANTE: Solo UN párrafo, máximo 5 líneas. No inventes datos que no aparezcan en la tabla. Mantén un tono periodístico pero conciso.`
      },
      {
        title: '📊 Análisis: El Estado del Campeonato',
        prompt: `ESTILO PERIODÍSTICO REQUERIDO: Redacta como un análisis deportivo de periódico, con párrafos fluidos que analicen la situación actual del campeonato.

Clasificación general actual de la ${ligaNombre}:\n${tablaClasif}

Redacta un análisis periodístico del estado actual del campeonato que incluya:

- Un párrafo inicial que describa lo cerrado de la lucha por el título
- Análisis individual de cada uno de los cinco primeros clasificados, explicando sus fortalezas y debilidades en párrafos separados
- Un párrafo dedicado a los pilotos que pueden dar la sorpresa desde posiciones intermedias
- Análisis de la dinámica del campeonato y qué factores pueden decidir el título
- Párrafo de cierre que genere expectativa sobre las próximas carreras

Escribe en estilo periodístico, con párrafos bien estructurados. Evita listas y bullets. Cada piloto debe tener su propio párrafo con análisis detallado de su situación.

IMPORTANTE: Usa SOLO los pilotos de la clasificación proporcionada.`
      },
      {
        title: '🏁 Circuito: Historia, Técnica y Curiosidades',
        prompt: `ESTILO PERIODÍSTICO REQUERIDO: Redacta como un artículo técnico especializado, con párrafos informativos y detallados.

Próximo circuito: ${circuitoNombre}

Redacta un análisis exhaustivo del circuito que incluya:

- Historia del trazado: orígenes, evolución, grandes momentos históricos
- Características técnicas detalladas: longitud, curvas clave, sectores, rectas, elevaciones
- Retos específicos para los pilotos: frenadas críticas, curvas técnicas, zonas de adelantamiento
- Curiosidades y anécdotas que lo hacen especial
- Records históricos y momentos memorables
- Datos meteorológicos típicos y cómo afectan al racing
- Por qué este circuito es único en el calendario

Escribe en párrafos extensos y detallados, como una enciclopedia especializada. Incluye todos los detalles técnicos e históricos que conozcas del circuito.

IMPORTANTE: Proporciona información rica en detalles técnicos e históricos. Este será el apartado más extenso del artículo.`
      },
      {
        title: '🔧 Setup del Gillet Vertigo: Guía Técnica',
        prompt: `ESTILO TÉCNICO REQUERIDO: Redacta como una guía técnica narrativa, combinando información técnica con párrafos fluidos y explicativos.
      
      Circuito: ${circuitoNombre}
      Coche: Gillet Vertigo (versión de competición)
      
      ${gilletVertigoInfo}
      
      INFORMACIÓN ADICIONAL DE CARRERAS:
      - Duración de carreras: 60 minutos
      - Clima: Aleatorio y cambiante durante la carrera
      - Sin conocimiento previo de condiciones meteorológicas
      
      Redacta una guía técnica narrativa para el setup del Gillet Vertigo en este circuito que incluya:
      
      - Párrafo de introducción analizando por qué el Vertigo es especial en este circuito específico
      - Setup de suspensión explicado en párrafos narrativos: por qué estas configuraciones, cómo afectan al comportamiento
      - Configuración aerodinámica con explicación del compromiso entre downforce y velocidad según las características del circuito
      - Setup de frenos: distribución, presión y discos recomendados, explicando la lógica detrás de cada decisión
      - Configuración de neumáticos con presiones y temperaturas, explicando cómo el peso ligero del Vertigo afecta el desgaste
      - Setup de transmisión y diferencial con justificación técnica para las curvas clave del circuito
      - Estrategia general para carreras de 60 minutos con clima cambiante (sin especificar paradas exactas ni combustible específico)
      - Párrafo de consejos de pilotaje específicos para aprovechar las fortalezas únicas del Vertigo
      - Párrafo de cierre con la filosofía de setup para este coche legendario
      
      IMPORTANTE: 
      - Escribe en párrafos narrativos, no en listas técnicas
      - No inventes datos específicos de combustible, vueltas de parada, o estrategias exactas
      - No incluyas nombres de autor ni créditos
      - Enfócate en explicar el "por qué" de cada configuración técnica
      - Considera que el clima es impredecible y las carreras duran 60 minutos
      - Mantén un tono técnico pero accesible, como un ingeniero experimentado explicando su filosofía de setup`
      },
      {
        title: '🔮 Predicciones: La Batalla que se Avecina',
        prompt: `ESTILO PERIODÍSTICO REQUERIDO: Redacta como un artículo de predicciones deportivas, con análisis fundamentado y narrativa que genere expectativa.

Próximo circuito: ${circuitoNombre}
Clasificación actual:\n${tablaClasif}
Coche de la temporada: Gillet Vertigo

Redacta un artículo de predicciones que incluya:

- Párrafo de apertura que plantee las incógnitas de la próxima carrera
- Análisis del favorito para la victoria, explicando por qué puede ganar
- Párrafos individuales para otros dos candidatos al podio
- Cómo las características del Gillet Vertigo pueden influir en la carrera
- Análisis de las estrategias clave que pueden decidir la carrera
- Un piloto "outsider" que puede dar la sorpresa
- Predicción final y párrafo de cierre que genere expectativa

Escribe en estilo periodístico deportivo, con párrafos que fluyan naturalmente. Fundamenta cada predicción en datos concretos del rendimiento de los pilotos y las características del circuito.

IMPORTANTE: Usa SOLO los pilotos de las tablas proporcionadas. Integra las características del Vertigo en el análisis.`
      }
    ];

    // Estado inicial
    setPromptsGen(prompts.map(p => ({ ...p, status: 'loading', result: '', error: '' })));

    // 3. Peticiones paralelas a DeepSeek
    await Promise.all(prompts.map(async (p, idx) => {
      try {
        console.log(`[PROMPT ${idx+1}] Enviando...`);
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: 'deepseek-chat',
            messages: [
              { 
                role: 'system', 
                content: 'Eres un periodista deportivo especializado en simracing y automovilismo. Tienes conocimientos técnicos profundos sobre circuitos, coches de competición y setups. Escribes artículos profesionales con estilo periodístico variado según el tema: conciso para resúmenes, detallado para análisis técnicos, y emocionante para predicciones.' 
              },
              { role: 'user', content: p.prompt }
            ],
            stream: false
          })
        });
        if (!res.ok) throw new Error('Error al conectar con la IA');
        const data = await res.json();
        const texto = data.choices?.[0]?.message?.content || 'Sin respuesta';
        setPromptsGen(prev => prev.map((pg, i) => i === idx ? { ...pg, status: 'success', result: texto, error: '' } : pg));
        console.log(`[PROMPT ${idx+1}] OK`);
      } catch (err: any) {
        setPromptsGen(prev => prev.map((pg, i) => i === idx ? { ...pg, status: 'error', error: err.message || 'Error desconocido' } : pg));
        console.error(`[PROMPT ${idx+1}] ERROR`, err);
      }
    }));

    // 4. Unir los textos
    const textos = promptsGen.map(pg => pg.result).filter(Boolean);
    if (textos.length === 5) {
      const articulo = textos.join('\n\n---\n\n');
      setOutput(articulo);
    } else {
      setOutput('No se pudo generar el artículo completo. Revisa los estados de cada sección.');
    }
    setLoading(false);
  };

  return (
    <PageContainer>
      <SectionHeaderComp icon={<FaRobot style={{ color: '#e4312e' }} />}>
        Redacción IA - Especial Gillet Vertigo 🏎️
      </SectionHeaderComp>
      
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '16px',
        borderRadius: '10px',
        marginBottom: '24px',
        textAlign: 'center'
      }}>
        <h3 style={{margin: '0 0 8px 0'}}>🏎️ Generador de Artículos - Gillet Vertigo</h3>
        <p style={{margin: 0, opacity: 0.9}}>
          Artículos técnicos especializados para la temporada del legendario Gillet Vertigo
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          <div>
            <label htmlFor="ligaSelect" style={{fontWeight: 'bold', marginBottom: '8px', display: 'block'}}>
              🏆 Selecciona una liga:
            </label>
            <select
              id="ligaSelect"
              value={selectedLigaId}
              onChange={e => {
                setSelectedLigaId(e.target.value);
                const nuevosCircuitos = Array.from(new Set(data.carreras.filter(c => c.ligaId === e.target.value).map(c => c.circuito).filter(Boolean)));
                setSelectedCircuit(nuevosCircuitos[0] || '');
              }}
              style={{ 
                width: '100%',
                padding: '12px', 
                borderRadius: '8px', 
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                background: 'white'
              }}
            >
              {ligas.map(liga => (
                <option key={liga.id} value={liga.id}>{liga.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="circuitSelect" style={{fontWeight: 'bold', marginBottom: '8px', display: 'block'}}>
              🏁 Selecciona un circuito:
            </label>
            <select
              id="circuitSelect"
              value={selectedCircuit}
              onChange={e => setSelectedCircuit(e.target.value)}
              style={{ 
                width: '100%',
                padding: '12px', 
                borderRadius: '8px', 
                fontSize: '1rem',
                border: '2px solid #e0e0e0',
                background: 'white'
              }}
            >
              {circuitos.length === 0 && <option value="">No hay circuitos disponibles</option>}
              {circuitos.map(circuito => (
                <option key={circuito} value={circuito}>{circuito}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{
          background: 'rgba(52, 152, 219, 0.1)',
          border: '2px solid rgba(52, 152, 219, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '16px'
        }}>
          <h4 style={{margin: '0 0 8px 0', color: '#3498db'}}>📋 Estructura del Artículo - Gillet Vertigo Edition</h4>
          <div style={{fontSize: '0.9em', color: '#666'}}>
            1. <strong>Resumen última carrera</strong> - Un párrafo conciso<br/>
            2. <strong>Estado del campeonato</strong> - Análisis de la clasificación actual<br/>
            3. <strong>Circuito completo</strong> - Historia, técnica y curiosidades detalladas<br/>
            4. <strong>Setup del Gillet Vertigo</strong> - Guía técnica especializada<br/>
            5. <strong>Predicciones</strong> - La batalla que se avecina
          </div>
        </div>

        {/* Info del Gillet Vertigo */}
        <div style={{
          background: 'rgba(228, 49, 46, 0.1)',
          border: '2px solid rgba(228, 49, 46, 0.3)',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '16px'
        }}>
          <h4 style={{margin: '0 0 8px 0', color: '#e4312e'}}>🏎️ Gillet Vertigo - El Legendario</h4>
          <div style={{fontSize: '0.9em', color: '#666'}}>
            <strong>Coche de la temporada:</strong> Gillet Vertigo (Monocasco de carbono 58kg, 0-100 en 3.26s)<br/>
            <strong>Categoría:</strong> GT2 early-2000s / G2 non-homologated GT<br/>
            <strong>Filosofía:</strong> "David contra Goliat" - El pequeño belga que venció a Porsche y Ferrari
          </div>
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? '🏎️ Redactando artículo del Vertigo...' : '🚀 Generar Artículo Gillet Vertigo'}
        </Button>
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </Form>

      {/* Estados de generación mejorados */}
      {promptsGen.length > 0 && (
        <div style={{margin: '32px 0', background: '#f8f9fa', borderRadius: '12px', padding: '24px'}}>
          <h3 style={{marginBottom: '20px', color: '#333'}}>📝 Progreso de Generación</h3>
          {promptsGen.map((pg, idx) => (
            <div key={idx} style={{
              border: '2px solid #e9ecef',
              borderRadius: '10px',
              marginBottom: '16px',
              padding: '20px',
              background: '#fff',
              transition: 'all 0.3s ease'
            }}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px'}}>
                <h4 style={{margin: 0, color: '#495057'}}>{pg.title}</h4>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  color: 'white',
                  background: pg.status === 'loading' ? '#17a2b8' : 
                           pg.status === 'success' ? '#28a745' : '#dc3545'
                }}>
                  {pg.status === 'loading' && '⏳ Generando...'}
                  {pg.status === 'success' && '✅ Completado'}
                  {pg.status === 'error' && `❌ Error: ${pg.error}`}
                </span>
              </div>
              
              {pg.result && (
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{fontSize:'0.9em', color:'#6c757d', marginBottom:'8px', fontWeight: 'bold'}}>
                    📄 Contenido generado:
                  </div>
                  <div style={{
                    fontSize:'1.05em', 
                    background:'#ffffff', 
                    borderRadius:'6px', 
                    padding:'16px', 
                    border:'1px solid #e9ecef',
                    lineHeight: '1.7',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {pg.result}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Datos de referencia en formato mejorado */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '32px'}}>
        {/* Clasificación general */}
        <div>
          <h3 style={{marginBottom: '16px', color: '#495057'}}>🏆 Clasificación General</h3>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background: 'linear-gradient(135deg, #e4312e, #c3251a)', color:'#fff'}}>
                  <th style={{padding:'12px 8px', fontSize: '0.9em'}}>Pos</th>
                  <th style={{padding:'12px 8px', fontSize: '0.9em'}}>Piloto</th>
                  <th style={{padding:'12px 8px', fontSize: '0.9em'}}>Puntos</th>
                </tr>
              </thead>
              <tbody>
                {clasificacion.slice(0, 10).map((row, idx) => (
                  <tr key={row.socioId} style={{
                    background: idx%2?'#f8f9fa':'#fff',
                    borderBottom: '1px solid #e9ecef'
                  }}>
                    <td style={{padding:'10px 8px', textAlign: 'center', fontWeight: 'bold'}}>
                      {idx < 3 ? ['🥇','🥈','🥉'][idx] : idx+1}
                    </td>
                    <td style={{padding:'10px 8px'}}>{row.nombre}</td>
                    <td style={{padding:'10px 8px', textAlign: 'center', fontWeight: 'bold', color: '#e4312e'}}>
                      {row.puntos}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Última carrera */}
        {ultimaCarrera && (
          <div>
            <h3 style={{marginBottom: '16px', color: '#495057'}}>
              🏁 Última Carrera
            </h3>
            <div style={{
              background: 'white',
              borderRadius: '10px',
              padding: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{marginBottom: '12px'}}>
                <strong>{ultimaCarrera.nombre}</strong><br/>
                <span style={{color: '#6c757d'}}>{ultimaCarrera.circuito} - {ultimaCarrera.fecha}</span>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize: '0.9em'}}>
                  <thead>
                    <tr style={{background:'#343a40', color:'#fff'}}>
                      <th style={{padding:'8px 4px'}}>P</th>
                      <th style={{padding:'8px 4px'}}>Piloto</th>
                      <th style={{padding:'8px 4px'}}>Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ultimaCarrera.resultados.sort((a,b)=>a.posicion-b.posicion).slice(0, 5).map((res, idx) => {
                      const socio = data.socios.find(s=>s.id===res.socioId);
                      return (
                        <tr key={res.id} style={{background: idx%2?'#f8f9fa':'#fff'}}>
                          <td style={{padding:'6px 4px', textAlign: 'center', fontWeight: 'bold'}}>
                            {res.posicion <= 3 ? ['🥇','🥈','🥉'][res.posicion-1] : res.posicion}
                          </td>
                          <td style={{padding:'6px 4px'}}>{socio?.display_name || socio?.nombre || 'Piloto'}</td>
                          <td style={{padding:'6px 4px', textAlign: 'center'}}>{res.puntosTotales ?? res.puntos}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Artículo final mejorado */}
      {promptsGen.filter(pg => pg.result).length > 0 && (
        <div style={{
          marginTop:'40px', 
          padding:'32px', 
          background:'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
          borderRadius:'16px', 
          border:'3px solid #28a745',
          boxShadow: '0 8px 25px rgba(40, 167, 69, 0.15)'
        }}>
          <div style={{textAlign: 'center', marginBottom: '24px'}}>
            <h2 style={{
              margin: '0 0 12px 0',
              color: '#28a745',
              fontSize: '1.8em'
            }}>
              🏎️ Artículo Gillet Vertigo Generado
            </h2>
            <p style={{
              margin: 0,
              color: '#6c757d',
              fontSize: '1.1em'
            }}>
              ¡Tu artículo especializado en el legendario Gillet Vertigo está listo!
            </p>
          </div>

          <div style={{display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '24px'}}>
            <button
              onClick={() => {
                const texto = promptsGen.map(pg => pg.result).filter(Boolean).join('\n\n---\n\n');
                navigator.clipboard.writeText(texto);
              }}
              style={{
                padding:'12px 24px', 
                borderRadius:'25px', 
                background:'#28a745', 
                color:'#fff', 
                border:'none', 
                fontWeight:'bold', 
                cursor:'pointer',
                fontSize: '1em',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              📋 Copiar Artículo Completo
            </button>
            
            <PublicarWarmUpButton promptsGen={promptsGen} liga={liga} circuito={selectedCircuit} />
          </div>

          <div style={{
            background:'#ffffff', 
            borderRadius:'12px', 
            padding:'24px', 
            border:'2px solid #e9ecef',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e9ecef'
            }}>
              <span style={{fontSize: '1.5em', marginRight: '12px'}}>🏎️</span>
              <div>
                <h4 style={{margin: 0, color: '#495057'}}>Artículo Gillet Vertigo Final</h4>
                <p style={{margin: 0, fontSize: '0.9em', color: '#6c757d'}}>
                  Listo para publicar • {promptsGen.filter(pg => pg.result).length}/5 secciones completadas
                </p>
              </div>
            </div>
            
            <div style={{
              fontSize:'1.1em', 
              background:'#f8f9fa', 
              borderRadius:'8px', 
              padding:'24px', 
              border:'1px solid #dee2e6',
              lineHeight: '1.7',
              margin: 0,
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap'
            }}>
              {promptsGen.map(pg => pg.result).filter(Boolean).join('\n\n---\n\n')}
            </div>
          </div>

          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'rgba(228, 49, 46, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(228, 49, 46, 0.3)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: '8px'}}>
              <span style={{fontSize: '1.2em', marginRight: '8px'}}>🏎️</span>
              <strong style={{color: '#e4312e'}}>Artículo Gillet Vertigo Especializado:</strong>
            </div>
            <ul style={{
              margin: '0',
              paddingLeft: '20px',
              color: '#495057',
              lineHeight: '1.6'
            }}>
              <li>📝 Resumen conciso de la última carrera (solo 1 párrafo)</li>
              <li>📊 Análisis detallado del estado del campeonato</li>
              <li>🏁 Historia y técnica exhaustiva del circuito</li>
              <li>🔧 Guía técnica especializada del setup del Gillet Vertigo</li>
              <li>🔮 Predicciones integradas con las características del Vertigo</li>
              <li>🏎️ Información técnica del legendario monocasco belga</li>
            </ul>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default RedaccionIACircuitos;